/**
 * Persists full local workspace snapshots (collections, variables, environments).
 *
 * Everything here is scoped to the CURRENT ACCOUNT NAMESPACE (see
 * services/storageNamespace.js):
 *   Tauri:   AppLocalData/local-workspaces/ns-<userId>/<workspaceId>/snapshot.json
 *   Browser: localStorage `apiboo-<userId>-local-snapshot-<workspaceId>`
 *
 * The namespace is part of every path and key this module builds, so a scan
 * run while account A is signed in cannot physically reach account B's
 * snapshots — there is no filtering step to get wrong. Signed out, the
 * namespace is `_local`; those workspaces are handed to an account only by the
 * explicit, re-keying adoptLocalNamespaceWorkspaces() below.
 */
import { isTauri as coreIsTauri, invoke } from '@tauri-apps/api/core';
import {
  LOCAL_NAMESPACE,
  apibooKey,
  getStorageNamespace,
  listStorageKeys,
} from './storageNamespace';
import { purgeLegacyDeviceTrees } from './accountData';
import { generateLocalWorkspaceId } from '../utils/workspaceStorage';
import { generateLocalEntityId } from '../utils/localIds';
import { buildWorkspacePublishPayload } from './workspacePublishPayload';

/** Cached result of whether AppLocalData FS read/write works (probed once). */
let fsStorageCache;

const SNAPSHOT_ROOT = 'local-workspaces';

/** Namespace directory segment. Sanitized: it becomes a path component. */
function nsSegment(ns) {
  return `ns-${String(ns).replace(/[^A-Za-z0-9_-]/g, '_')}`;
}

/** `local-workspaces/ns-<ns>` — the only root any read or write may touch. */
function nsRoot(ns = getStorageNamespace()) {
  return `${SNAPSHOT_ROOT}/${nsSegment(ns)}`;
}

function manifestPath(ns = getStorageNamespace()) {
  return `${nsRoot(ns)}/manifest.json`;
}

/** Relative AppLocalData dir for one workspace's snapshot (also used by the SQLite migration). */
export function snapshotDirRelative(localId, ns = getStorageNamespace()) {
  return `${nsRoot(ns)}/${localId}`;
}

function snapshotPath(localId, ns = getStorageNamespace()) {
  return `${snapshotDirRelative(localId, ns)}/snapshot.json`;
}

/**
 * ABSOLUTE path of a local workspace's snapshot directory, for the callers that
 * hand a path to Rust (`reveal_in_explorer`, the SQLite migration's backup
 * source).
 *
 * This lives on the JS side because the account namespace segment does: Rust
 * has no way to know which `ns-<namespace>` a workspace id belongs to, so a
 * command that rebuilds this path itself can only guess — which is exactly how
 * "Reveal in Explorer" ended up pointing at a directory that never exists.
 */
export async function snapshotDirAbsolute(localId, ns = getStorageNamespace()) {
  const { appLocalDataDir, join } = await import('@tauri-apps/api/path');
  return join(await appLocalDataDir(), ...snapshotDirRelative(localId, ns).split('/'));
}

const browserManifestKey = (ns = getStorageNamespace()) => apibooKey('local-ws-manifest', ns);
const browserSnapshotKey = (localId, ns = getStorageNamespace()) =>
  apibooKey(`local-snapshot-${localId}`, ns);
const browserSnapshotPrefix = (ns = getStorageNamespace()) => apibooKey('local-snapshot-', ns);
/**
 * Cloud (backend-owned) workspaces keep only their Smart Mock schema tree on
 * device. It lives under its own key so a cloud workspace never mints a local
 * workspace: nothing writes `local-workspaces/<id>/`, so nothing can later
 * mistake it for a local workspace to be listed and adopted.
 */
const browserCloudSchemasKey = (workspaceId, ns = getStorageNamespace()) =>
  apibooKey(`cloud-schemas-${workspaceId}`, ns);

function emptySnapshot() {
  return {
    version: 1,
    workspaceName: '',
    collections: [],
    globalVariables: [],
    workspaceVariables: [],
    environments: [],
    selectedEnvironmentId: '',
    // Smart Mock schemas tree { folders, items } — local-only.
    schemas: { folders: [], items: [] },
  };
}

/** Valid `{ folders, items }` schemas shape, or empty. */
function normalizeSchemasField(raw) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return { folders: [], items: [] };
  }
  return {
    folders: Array.isArray(raw.folders) ? raw.folders : [],
    items: Array.isArray(raw.items) ? raw.items : [],
  };
}

function isEmptySchemas(s) {
  return !s || ((s.folders?.length ?? 0) === 0 && (s.items?.length ?? 0) === 0);
}

async function fsModule() {
  return import('@tauri-apps/plugin-fs');
}

function mightBeTauriRuntime() {
  if (typeof window === 'undefined') return false;
  try {
    if (coreIsTauri()) return true;
  } catch {
    // ignore
  }
  return (
    window.__TAURI__ !== undefined ||
    window.__TAURI_INTERNALS__ !== undefined ||
    window.location?.protocol === 'tauri:' ||
    (typeof navigator !== 'undefined' && navigator.userAgent.includes('Tauri'))
  );
}

/** Use filesystem (AppData) when running in Tauri and FS plugin is available. */
export async function prefersFilesystemStorage() {
  if (fsStorageCache !== undefined) return fsStorageCache;
  if (!mightBeTauriRuntime()) {
    fsStorageCache = false;
    return false;
  }
  try {
    const fs = await fsModule();
    await fs.mkdir(SNAPSHOT_ROOT, {
      baseDir: fs.BaseDirectory.AppLocalData,
      recursive: true,
    });
    fsStorageCache = true;
  } catch (err) {
    console.warn('[localWorkspaceStore] Using browser storage; AppData unavailable:', err);
    fsStorageCache = false;
  }
  if (fsStorageCache) await purgeLegacyDeviceTrees();
  return fsStorageCache;
}

function scanBrowserSnapshotIds(known, ns) {
  const found = [];
  const prefix = browserSnapshotPrefix(ns);
  for (const key of listStorageKeys()) {
    if (!key.startsWith(prefix)) continue;
    const id = key.slice(prefix.length);
    if (id && !known.has(String(id))) found.push(id);
  }
  return found;
}

async function scanFsSnapshotIds(known, ns) {
  const found = [];
  const root = nsRoot(ns);
  try {
    const fs = await fsModule();
    const entries = await fs.readDir(root, {
      baseDir: fs.BaseDirectory.AppLocalData,
    });
    for (const entry of entries) {
      if (!entry?.name || entry.name === 'manifest.json') continue;
      const id = entry.name;
      if (known.has(String(id))) continue;
      try {
        await fs.readTextFile(`${root}/${id}/snapshot.json`, {
          baseDir: fs.BaseDirectory.AppLocalData,
        });
        found.push(id);
      } catch {
        // not a workspace folder
      }
    }
  } catch {
    // namespace dir does not exist yet — nothing to self-heal
  }
  return found;
}

/**
 * Normalize snapshot loaded from disk (migrate minimal shapes).
 */
export function normalizeSnapshot(raw) {
  if (!raw || typeof raw !== 'object') return emptySnapshot();
  return {
    version: raw.version ?? 1,
    workspaceName: typeof raw.workspaceName === 'string' ? raw.workspaceName : '',
    collections: Array.isArray(raw.collections) ? raw.collections : [],
    globalVariables: Array.isArray(raw.globalVariables) ? raw.globalVariables : [],
    workspaceVariables: Array.isArray(raw.workspaceVariables) ? raw.workspaceVariables : [],
    environments: Array.isArray(raw.environments) ? raw.environments : [],
    selectedEnvironmentId: raw.selectedEnvironmentId ?? '',
    schemas: normalizeSchemasField(raw.schemas),
  };
}

async function ensureDirFs(subPath) {
  const fs = await fsModule();
  const BaseDirectory = fs.BaseDirectory;
  const segments = subPath.replace(/\\/g, '/').split('/').filter(Boolean);
  let built = '';
  for (const seg of segments) {
    built = built ? `${built}/${seg}` : seg;
    try {
      await fs.mkdir(built, {
        baseDir: BaseDirectory.AppLocalData,
        recursive: true,
      });
    } catch {
      // ignore if exists
    }
  }
}

/**
 * Meta for a snapshot found inside the CURRENT namespace. `role: 'owner'` is
 * correct here precisely because the namespace already answered "whose is
 * this?" — the id was read out of the signed-in account's own key space.
 */
function metaFromLocalId(id, snap, existingMeta) {
  const name =
    existingMeta?.name ||
    snap?.workspaceName ||
    `Local (${String(id).slice(0, 8)})`;
  return {
    id,
    local_uid: existingMeta?.local_uid ?? id,
    name,
    description: existingMeta?.description || '',
    type: 'personal',
    storage: 'local',
    source: 'personal',
    role: 'owner',
  };
}

/** Snapshot folders / keys inside `ns` that are not listed in its manifest. */
async function discoverOrphanLocalWorkspaceIds(manifestIds, ns) {
  const known = new Set((manifestIds || []).map((id) => String(id)));
  const found = new Set();

  if (await prefersFilesystemStorage()) {
    for (const id of await scanFsSnapshotIds(known, ns)) {
      found.add(id);
      known.add(String(id));
    }
  }

  for (const id of scanBrowserSnapshotIds(known, ns)) {
    found.add(id);
  }

  return [...found];
}

function readManifestFromBrowser(ns) {
  try {
    const raw = localStorage.getItem(browserManifestKey(ns));
    const list = raw ? JSON.parse(raw) : [];
    return Array.isArray(list) ? list : [];
  } catch {
    return [];
  }
}

async function readManifestFromFs(ns) {
  try {
    const fs = await fsModule();
    const text = await fs.readTextFile(manifestPath(ns), {
      baseDir: fs.BaseDirectory.AppLocalData,
    });
    const list = JSON.parse(text);
    return Array.isArray(list) ? list : [];
  } catch {
    return [];
  }
}

export async function listLocalWorkspaceMetasFromManifest(ns = getStorageNamespace()) {
  const useFs = await prefersFilesystemStorage();
  const fromFs = useFs ? await readManifestFromFs(ns) : [];
  const fromBrowser = readManifestFromBrowser(ns);

  const map = new Map();
  for (const meta of [...fromBrowser, ...fromFs]) {
    if (meta?.id != null) map.set(String(meta.id), meta);
  }
  return [...map.values()];
}

/**
 * Manifest entries plus any snapshot directories not yet in the manifest
 * (self-heal after reload). Both halves are read from the current namespace
 * only, so this can never surface another account's workspace.
 */
export async function listLocalWorkspaceMetasResolved(ns = getStorageNamespace()) {
  const fromManifest = await listLocalWorkspaceMetasFromManifest(ns);
  const manifestIds = fromManifest.map((m) => m.id);
  const orphans = await discoverOrphanLocalWorkspaceIds(manifestIds, ns);

  const map = new Map();
  for (const meta of fromManifest) {
    if (meta?.id != null) map.set(String(meta.id), meta);
  }

  for (const id of orphans) {
    const snap = await loadSnapshot(id, ns);
    map.set(String(id), metaFromLocalId(id, snap, null));
  }

  const resolved = [...map.values()];
  if (
    orphans.length > 0 ||
    ((await prefersFilesystemStorage()) && fromManifest.length === 0 && resolved.length > 0)
  ) {
    try {
      await saveLocalWorkspaceManifest(resolved, ns);
    } catch (err) {
      console.error('Failed to repair local workspace manifest:', err);
    }
  }
  return resolved;
}

export async function saveLocalWorkspaceManifest(metaList, ns = getStorageNamespace()) {
  const payload = JSON.stringify(metaList ?? []);
  localStorage.setItem(browserManifestKey(ns), payload);
  if (await prefersFilesystemStorage()) {
    const fs = await fsModule();
    await ensureDirFs(nsRoot(ns));
    await fs.writeTextFile(manifestPath(ns), payload, {
      baseDir: fs.BaseDirectory.AppLocalData,
    });
  }
}

export async function loadSnapshot(localId, ns = getStorageNamespace()) {
  if (!localId) return emptySnapshot();

  if (await prefersFilesystemStorage()) {
    try {
      const fs = await fsModule();
      const text = await fs.readTextFile(snapshotPath(localId, ns), {
        baseDir: fs.BaseDirectory.AppLocalData,
      });
      return normalizeSnapshot(JSON.parse(text));
    } catch {
      // fall through to browser
    }
  }

  try {
    const raw = localStorage.getItem(browserSnapshotKey(localId, ns));
    return raw ? normalizeSnapshot(JSON.parse(raw)) : emptySnapshot();
  } catch {
    return emptySnapshot();
  }
}

export function mergeSnapshotField(partialValue, currentValue, { skipEmptyArrays = false } = {}) {
  if (partialValue === undefined) return currentValue;
  if (
    skipEmptyArrays &&
    Array.isArray(partialValue) &&
    partialValue.length === 0 &&
    Array.isArray(currentValue) &&
    currentValue.length > 0
  ) {
    return currentValue;
  }
  return partialValue;
}

/**
 * @param {string} localId
 * @param {object} partial
 * @param {{ skipEmptyArrays?: boolean, namespace?: string }} [options] - skipEmptyArrays: empty arrays do not overwrite non-empty stored arrays (avoids pre-hydration wipes).
 */
export async function saveSnapshot(localId, partial, options = {}) {
  if (!localId) return;

  const ns = options.namespace ?? getStorageNamespace();
  const skipEmpty = options.skipEmptyArrays === true;
  const current = await loadSnapshot(localId, ns);
  const merged = normalizeSnapshot({
    ...current,
    ...partial,
    collections: mergeSnapshotField(partial.collections, current.collections, {
      skipEmptyArrays: skipEmpty,
    }),
    globalVariables: mergeSnapshotField(partial.globalVariables, current.globalVariables, {
      skipEmptyArrays: skipEmpty,
    }),
    workspaceVariables: mergeSnapshotField(
      partial.workspaceVariables,
      current.workspaceVariables,
      { skipEmptyArrays: skipEmpty }
    ),
    environments: mergeSnapshotField(partial.environments, current.environments, {
      skipEmptyArrays: skipEmpty,
    }),
    schemas: (() => {
      if (partial.schemas === undefined) return current.schemas;
      const next = normalizeSchemasField(partial.schemas);
      // During pre-hydration a default empty tree must not wipe a loaded one.
      if (skipEmpty && isEmptySchemas(next)) return current.schemas;
      return next;
    })(),
    selectedEnvironmentId:
      partial.selectedEnvironmentId !== undefined
        ? partial.selectedEnvironmentId
        : current.selectedEnvironmentId,
    workspaceName:
      partial.workspaceName !== undefined && partial.workspaceName !== ''
        ? partial.workspaceName
        : current.workspaceName,
  });

  const payload = JSON.stringify(merged);

  localStorage.setItem(browserSnapshotKey(localId, ns), payload);

  if (await prefersFilesystemStorage()) {
    const fs = await fsModule();
    await ensureDirFs(snapshotDirRelative(localId, ns));
    await fs.writeTextFile(snapshotPath(localId, ns), payload, {
      baseDir: fs.BaseDirectory.AppLocalData,
    });
  }
}

export async function removeLocalWorkspaceFiles(localId, ns = getStorageNamespace()) {
  if (!localId) return;
  localStorage.removeItem(browserSnapshotKey(localId, ns));
  if (!(await prefersFilesystemStorage())) return;
  try {
    const fs = await fsModule();
    await fs.remove(snapshotDirRelative(localId, ns), {
      baseDir: fs.BaseDirectory.AppLocalData,
      recursive: true,
    });
  } catch {
    // ignore
  }
}

/* ── Cloud workspace schemas (no local workspace is created) ────────────── */

/** Smart Mock schema tree for a CLOUD workspace. Browser key only — no snapshot folder. */
export function loadCloudSchemas(workspaceId) {
  if (!workspaceId) return normalizeSchemasField(null);
  try {
    const raw = localStorage.getItem(browserCloudSchemasKey(workspaceId));
    return normalizeSchemasField(raw ? JSON.parse(raw) : null);
  } catch {
    return normalizeSchemasField(null);
  }
}

export function saveCloudSchemas(workspaceId, schemas) {
  if (!workspaceId) return;
  try {
    localStorage.setItem(
      browserCloudSchemasKey(workspaceId),
      JSON.stringify(normalizeSchemasField(schemas))
    );
  } catch {
    // ignore (quota)
  }
}

/* ── Signed-out → sign-in adoption ──────────────────────────────────────── */

/**
 * `{ [localWorkspaceId]: { [userId]: newWorkspaceId } }` — which accounts have
 * already adopted which `_local` workspace. Kept in its own key rather than on
 * the `_local` manifest entries: the manifest is rewritten wholesale whenever
 * the workspace list changes (including the empty rewrite a logout reset
 * produces), and losing this record would silently duplicate the workspace on
 * the next sign-in.
 */
const adoptionsKey = () => apibooKey('local-adoptions', LOCAL_NAMESPACE);

function readAdoptions() {
  try {
    const raw = localStorage.getItem(adoptionsKey());
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

function writeAdoptions(map) {
  try {
    localStorage.setItem(adoptionsKey(), JSON.stringify(map));
  } catch {
    // ignore (quota)
  }
}

/**
 * Tell the Rust hand-off route that this workspace is now OURS, so it does not
 * adopt the same `_local` workspace a second time.
 *
 * There are two adoption paths and they do not share storage: this one copies
 * `_local`'s FILE snapshots into the account's namespace, while
 * `device_migration::merge_into` copies `_local`'s SQLITE rows into the
 * account's database. `workspaceStoreRouter.js` dual-writes every workspace to
 * both stores, so both paths can see the same workspace — and now that both
 * re-key (`rekeySnapshotIds` here, `IdRemap` there), the fresh random ids can
 * never collide, so nothing suppresses the duplicate any more. Signing in runs
 * this adoption automatically; clicking "Bring it in" in Settings afterwards
 * then produced a SECOND copy of the same workspace under a disjoint id set,
 * and pushed it to the server.
 *
 * This path wins, because it is the automatic one that always runs first —
 * see `JS_CLAIM_PREF_PREFIX` in `src-tauri/src/device_migration.rs` for the
 * full rule and for what the Rust route still moves (extension documents,
 * which this path never touches, re-pointed at the id minted here).
 *
 * Best-effort by design: the snapshot is already written by the time this runs,
 * so a failure here costs a possible duplicate offer, never the workspace. It
 * is deliberately NOT ordered before the write — a claim recorded for a
 * workspace that then failed to save would make BOTH paths skip it.
 */
async function noteAdoptionForHandoff(localWorkspaceId, adoptedAs) {
  if (!coreIsTauri()) return;
  try {
    await invoke('local_handoff_note_js_adoption', {
      localWorkspaceId: String(localWorkspaceId),
      adoptedAs: String(adoptedAs),
    });
  } catch (err) {
    console.warn(
      '[localWorkspaceStore] could not record the local-workspace adoption with the Rust hand-off route — Settings may offer to bring the same workspace in again:',
      err
    );
  }
}

/**
 * Every id-carrying value inside a snapshot, collected into `out`.
 *
 * Mirrors the shapes the snapshot producers actually write:
 *  - `collections[]`: `id`, `local_uid`, `settings.folders[].id`
 *    (`utils/collectionFolders.js`)
 *  - `collections[].requests[]`: `id`, `mock.responseExamples[].id`
 *  - `environments[]`: `id`
 *  - `schemas.folders[]`: `id`; `schemas.items[]`: `id` plus a recursive
 *    `nodes[]` tree of `{ id, refSchemaId }` (`utils/schemaTree.js`)
 *
 * References (`parent_id`, `folder_id`, `settings.folder_id`, `refSchemaId`,
 * `selectedEnvironmentId`, `mock.responseExamples[].schemaId`,
 * `_localWorkspaceId`) are deliberately NOT collected: they are rewritten by
 * value in `rekeySnapshotIds` below, so each one follows whatever its
 * referent got without this function having to know the path it lives at.
 */
function collectSnapshotIds(snap, out) {
  const add = (v) => {
    if (typeof v === 'string' && v) out.add(v);
  };
  const walkNodes = (nodes) => {
    for (const n of Array.isArray(nodes) ? nodes : []) {
      if (!n || typeof n !== 'object') continue;
      add(n.id);
      walkNodes(n.children);
    }
  };

  for (const c of snap.collections || []) {
    if (!c || typeof c !== 'object') continue;
    add(c.id);
    add(c.local_uid);
    for (const f of c.settings?.folders || []) add(f?.id);
    for (const r of c.requests || []) {
      if (!r || typeof r !== 'object') continue;
      add(r.id);
      for (const ex of r.mock?.responseExamples || []) add(ex?.id);
    }
  }
  for (const e of snap.environments || []) add(e?.id);
  for (const f of snap.schemas?.folders || []) add(f?.id);
  for (const it of snap.schemas?.items || []) {
    if (!it || typeof it !== 'object') continue;
    add(it.id);
    walkNodes(it.nodes);
  }
  return out;
}

/** Deep structural copy of `value` with every string that is a key of `map` replaced. */
function replaceIdsDeep(value, map) {
  if (typeof value === 'string') return map.has(value) ? map.get(value) : value;
  if (Array.isArray(value)) return value.map((v) => replaceIdsDeep(v, map));
  if (value && typeof value === 'object') {
    const out = {};
    for (const [k, v] of Object.entries(value)) out[k] = replaceIdsDeep(v, map);
    return out;
  }
  return value;
}

/** A fresh id, keeping any `<prefix>-` the old one carried (`fld-`, `sch-`, …). */
function mintReplacementId(oldId) {
  const prefix = /^([A-Za-z]+)-/.exec(String(oldId))?.[1];
  const fresh = generateLocalEntityId(prefix || 'loc');
  return prefix && !fresh.startsWith(`${prefix}-`) ? `${prefix}-${fresh}` : fresh;
}

/**
 * Re-key EVERY id in `snap`, not just the workspace's own.
 *
 * Adoption already minted a fresh workspace id — but wrote `{...snap}`
 * unchanged underneath it, so two accounts adopting the same `_local`
 * workspace ended up holding identical collection, request, folder,
 * environment and schema ids. That is the same defect the Rust merge route
 * carries (`device_migration::merge_into`), for the same reason: an id minted
 * while signed out is not the property of the first account to claim it, and
 * `_local` is deliberately left in place so a SECOND account can claim it too.
 *
 * References are rewritten by VALUE, at any depth — so `folder_id`,
 * `settings.folder_id`, `parent_id`, `refSchemaId`, `schemaId`,
 * `selectedEnvironmentId` and `_localWorkspaceId` all follow their referents
 * without this function enumerating where they live. A string that is not one
 * of this snapshot's own ids is left exactly as it was.
 *
 * @param {object} snap normalized snapshot
 * @param {string} oldWorkspaceId the `_local` workspace id being adopted
 * @param {string} newWorkspaceId the id it is being adopted under
 * @returns {object} a new snapshot; `snap` is never mutated
 */
export function rekeySnapshotIds(snap, oldWorkspaceId, newWorkspaceId) {
  const source = normalizeSnapshot(snap);
  const map = new Map();
  if (oldWorkspaceId && newWorkspaceId) map.set(String(oldWorkspaceId), String(newWorkspaceId));
  for (const oldId of collectSnapshotIds(source, new Set())) {
    if (map.has(oldId)) continue;
    map.set(oldId, mintReplacementId(oldId));
  }
  return normalizeSnapshot(replaceIdsDeep(source, map));
}

/** A workspace with nothing in it (the auto-created "Local" scratch) is not worth adopting. */
function isAdoptableSnapshot(snap) {
  if (!snap) return false;
  const meaningfulGlobals = (snap.globalVariables || []).filter((v) => v?.key !== 'timestamp');
  return (
    (snap.collections?.length ?? 0) > 0 ||
    (snap.environments?.length ?? 0) > 0 ||
    (snap.workspaceVariables?.length ?? 0) > 0 ||
    meaningfulGlobals.length > 0 ||
    !isEmptySchemas(snap.schemas)
  );
}

/**
 * Hand the signed-out (`_local`) workspaces to the account that just signed
 * in. Called ONLY from the explicit guest → sign-in transition; there is no
 * ambient scan anywhere that could do this by accident.
 *
 * Each adopted workspace gets a BRAND NEW id in the adopting account's
 * namespace — and so does everything INSIDE it (`rekeySnapshotIds`: every
 * collection, request, folder, environment, schema and schema node), matching
 * the rule the Rust side applies on both of its own adoption routes
 * (`sync::records::personal_migrate_local_scope_in`,
 * `device_migration::merge_into`). No id can then be shared between the
 * `_local` scratch space and an account, or between two accounts adopting the
 * same `_local` workspace. The `_local` original is left untouched and still
 * belongs to the signed-out session; the adoption is recorded on it so a later
 * sign-in by the same account does not duplicate it.
 *
 * @param {string|number} userId
 * @returns {Promise<Array<object>>} newly adopted workspace metas (new ids)
 */
export async function adoptLocalNamespaceWorkspaces(userId) {
  const target = String(userId ?? '');
  if (!target || target === LOCAL_NAMESPACE) return [];

  const localMetas = await listLocalWorkspaceMetasResolved(LOCAL_NAMESPACE);
  if (localMetas.length === 0) return [];

  const adoptions = readAdoptions();
  const adopted = [];

  for (const meta of localMetas) {
    if (adoptions[meta.id]?.[target]) continue;
    const snap = await loadSnapshot(meta.id, LOCAL_NAMESPACE);
    if (!isAdoptableSnapshot(snap)) continue;

    const newId = generateLocalWorkspaceId();
    const name = meta.name || snap.workspaceName || 'Local';
    // Re-key the CONTENTS too, not just the workspace wrapper — see
    // `rekeySnapshotIds`. Writing `{...snap}` here left every collection,
    // request, folder, environment and schema id shared with `_local` (and
    // therefore with every other account that adopts the same workspace).
    const rekeyed = rekeySnapshotIds(snap, meta.id, newId);
    await saveSnapshot(
      newId,
      { ...rekeyed, workspaceName: name },
      { skipEmptyArrays: false, namespace: target }
    );
    // After the write, never before — see `noteAdoptionForHandoff`.
    await noteAdoptionForHandoff(meta.id, newId);

    adopted.push({
      id: newId,
      local_uid: newId,
      name,
      description: meta.description || '',
      type: 'personal',
      storage: 'local',
      source: 'personal',
      role: 'owner',
      adopted_from: meta.id,
    });
    adoptions[meta.id] = { ...(adoptions[meta.id] || {}), [target]: newId };
  }

  if (adopted.length === 0) return [];

  writeAdoptions(adoptions);
  const existing = await listLocalWorkspaceMetasFromManifest(target);
  await saveLocalWorkspaceManifest([...existing, ...adopted], target);
  return adopted;
}

/**
 * Payload for `POST /workspaces/publish-local` from a LOCAL workspace snapshot
 * — publishing a local workspace to the cloud, and forking one.
 *
 * **This is a same-account transfer.** The endpoint creates a workspace owned by
 * the signed-in user, in that user's own account, private. Credentials in
 * `auth` / `headers` / `params` / `cookies` / `body` / `settings` travel
 * **deliberately and verbatim**: this is cloud sync reached through a different
 * button, and a copy of your own workspace with its secrets stripped is a broken
 * copy. Do not reach for `utils/shareRedaction.js` here — redaction belongs at
 * the *share* boundary, where a workspace crosses to somebody else (settled
 * 26.08.2026; re-confirmed 27.08.2026).
 *
 * `visibility` used to be an option on this function, and honouring `'public'`
 * was the only thing standing between the above and a completely different
 * threat model — the backend takes the field verbatim. Nothing ever passed
 * `'public'`; the branch is gone and the value is a constant in
 * `buildWorkspacePublishPayload`. Publishing to the public network is a separate
 * server-side route that redacts (`POST /workspaces/:id/publish-to-network`).
 *
 * The wire shape itself lives in `services/workspacePublishPayload.js`, shared
 * with `components/ImportNetworkModal.js`, which posts to the same endpoint.
 * That module's explicit key allowlist is what keeps the local-only bookkeeping
 * on these collections (`_deviceLocal`, `_localWorkspaceId`,
 * `cloud_collection_id`, `link_status`, `last_synced_at`, `local_uid`) off the
 * wire.
 */
export function buildPublishLocalPayload(workspaceMeta, snapshot) {
  return buildWorkspacePublishPayload({
    name: workspaceMeta.name || 'Local',
    description: workspaceMeta.description || '',
    variableScopes: {
      globals: snapshot.globalVariables,
      workspace_globals: snapshot.workspaceVariables,
      environments: snapshot.environments,
    },
    collections: snapshot.collections,
  });
}
